import { useLocation } from 'react-router';
import { Settings, LogOut } from 'lucide-react';
import { usePostHog } from '@posthog/react';
import { useAuth } from '#stores/AuthContext';
import { CollapseIcon, ExpandIcon } from '#assets/icons/icons';
import mainNavItems from '#constants/sideNavConstants';
import { Logo } from '#components/ui';
import NavItem, { NavItemProps } from './NavItem';

interface SideNavProps {
  isCollapsed: boolean;
  toggleCollapse: () => void;
}

const SideNav = ({ isCollapsed, toggleCollapse }: SideNavProps) => {
  const { user, logout } = useAuth();
  const posthog = usePostHog();

  const location = useLocation();
  const openSettings = () => {
    const dlg = document.getElementById(
      'settings-modal'
    ) as HTMLDialogElement | null;
    dlg?.showModal();
  };
  const bottomNavItems = [
    {
      label: 'Settings',
      icon: <Settings className="w-4 h-4" />,
      to: '#',
      onClick: openSettings,
    },
    {
      label: 'Sign Out',
      icon: <LogOut className="w-4 h-4" />,
      to: '/',
      onClick: () => {
        posthog.capture('user_logged_out');
        posthog.reset();
        logout();
      },
    },
  ];
  // Hide Admin section if user is not an admin
  const filteredNavItems = mainNavItems.filter((item) => {
    const isAdminRoute = item.to.startsWith('/dashboard/admin');
    return isAdminRoute ? Boolean(user?.isAdmin) : true;
  });

  return (
    <>
      <div>
        <div
          className={`flex w-full items-center justify-center ${isCollapsed ? 'flex-col-reverse' : 'flex-row'}`}
        >
          <div className="flex w-full">
            <div className="flex gap-2">
              {!isCollapsed ? (
                <div className="flex flex-col gap-2">
                  <Logo color="orange" className="h-16 w-auto" />
                </div>
              ) : (
                <div className="bg-[#191919] text-white rounded-full w-8 h-8 flex items-center justify-center">
                  {user?.companyName?.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            tabIndex={0}
            onClick={toggleCollapse}
            className={` hidden items-center cursor-pointer ${isCollapsed ? 'justify-center pt-6' : 'w-2'}`}
          >
            {!isCollapsed ? (
              <div>
                <CollapseIcon />
              </div>
            ) : (
              <ExpandIcon />
            )}
          </button>
        </div>

        <hr
          className={`border-t border-(--light-gray) mt-2 mb-6 ${isCollapsed ? '-mx-2' : '-mx-7'}`}
        />
        <div className="h-[calc(100vh-250px)] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400">
          <ul
            className={`menu flex flex-col  gap-y-3 w-full ${!isCollapsed ? 'p-0' : 'items-center justify-center px-0'}`}
          >
            <li className="text-base font-bold pl-2 text-neutral-600">
              {user?.companyName}
            </li>
            {filteredNavItems.map((item) => (
              <div key={`nav-item-${item.to}`}>
                <NavItem
                  key={item.to}
                  label={item.label}
                  icon={item.icon}
                  to={item.to}
                  isActive={location.pathname.includes(item.to)}
                  isParent
                  isCollapsed={isCollapsed}
                  subMenu={item.subMenu as NavItemProps[]}
                />
              </div>
            ))}
            {bottomNavItems.map((item) => (
              <div key={`nav-item-${item.label}`}>
                <NavItem
                  key={item.label}
                  label={item.label}
                  icon={item.icon}
                  to={item.to}
                  isActive={false}
                  isCollapsed={isCollapsed}
                  onClick={item.onClick}
                />
              </div>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
};

export default SideNav;
